import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { useSubmitOT } from '../hooks';
import { useToast } from '../components/ui/toast';
import { Modal } from '../components/ui/Modal';
import { useState } from 'react';

interface FormData {
  date: string;
  start_time: string;
  end_time: string;
  reason: string;
}

// Response shape from POST /ot (kept loose to avoid coupling)
interface OTSubmitResult {
  ot_id: string;
  status: string;
  hours?: number;
}

export function OTSubmit() {
  const navigate = useNavigate();
  const submit = useSubmitOT();
  const { register, handleSubmit } = useForm<FormData>();
  const [submitted, setSubmitted] = useState<OTSubmitResult | null>(null);
  const toast = useToast();

  const onSubmit = async (data: FormData) => {
    try {
      const res = (await submit.mutateAsync(data)) as OTSubmitResult;
      setSubmitted(res);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  };

  return (
    <PageLayout title="ขอ OT">
      <form onSubmit={handleSubmit(onSubmit)} className="card p-4 space-y-4">
        <div>
          <label className="label">วันที่</label>
          <input type="date" {...register('date', { required: true })} className="input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">เริ่ม</label>
            <input type="time" {...register('start_time', { required: true })} className="input" />
          </div>
          <div>
            <label className="label">สิ้นสุด</label>
            <input type="time" {...register('end_time', { required: true })} className="input" />
          </div>
        </div>
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-gray-700">
          ℹ️ ระบบคำนวณเรต OT ให้อัตโนมัติตามวันที่และเวลาที่ทำ (วันทำงาน 1.5 เท่า ·
          วันหยุดในเวลาปกติ · วันหยุดนอกเวลา 3 เท่า) ตามกฎหมายแรงงาน — ไม่ต้องเลือกประเภทเอง กันคิดผิด
        </div>
        <div>
          <label className="label">เหตุผล</label>
          <textarea {...register('reason', { required: true })} rows={3} className="input" />
        </div>
        <button type="submit" disabled={submit.isPending} className="btn-cta w-full">
          {submit.isPending ? 'กำลังส่ง...' : 'ส่งคำขอ'}
        </button>
      </form>

      {/* Success sheet — non-dismissible (must tap a button) so the user
          doesn't accidentally swipe it away and lose context of what they
          just submitted. */}
      <Modal open={!!submitted} onClose={() => navigate('/', { replace: true })}>
        <div className="text-center pt-2">
          <CheckCircle className="w-12 h-12 mx-auto mb-2 text-[#2f7d52]" />
          <h2 className="text-lg font-bold mb-1">ส่งคำขอ OT เรียบร้อย</h2>
          <p className="text-xs text-gray-400 font-mono mb-3">{submitted?.ot_id}</p>
          <p className="text-sm text-gray-600 mb-1">
            สถานะ: {submitted?.status === 'pending_L1' ? 'รอหัวหน้างานอนุมัติ' : submitted?.status}
          </p>
          {submitted?.hours !== undefined && (
            <p className="text-sm text-gray-600 mb-1">รวม {submitted.hours} ชั่วโมง</p>
          )}
          <p className="text-xs text-gray-500 mt-2">
            คุณจะได้รับแจ้งทาง LINE เมื่อมีการอัปเดต
          </p>
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => navigate('/', { replace: true })}
              className="btn-secondary flex-1"
            >
              เสร็จ
            </button>
            <button
              onClick={() => navigate('/me/requests', { replace: true })}
              className="btn-primary flex-1"
            >
              📋 ดูคำขอของฉัน
            </button>
          </div>
        </div>
      </Modal>
    </PageLayout>
  );
}
